import { DOCUMENT } from '@angular/common';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NzModalService } from 'ng-zorro-antd/modal';
import { catchError, throwError } from 'rxjs';
import { SigninService } from '../service/signin.service';
import { ErrorHandleComponent } from '../layouts/shared/error-handle-component/error-handle-component';

export interface ErrorModalData {
  title: string;
  message: string;
}

let connectionErrorShown = false;
let sessionExpiredShown = false;
let errorModalShown = false;

let lastErrorMessage = '';
let lastErrorTime = 0;

const ERROR_POPUP_COOLDOWN = 10_000;

function getModalWidth(document: Document): string | number {
  const windowWidth = document.defaultView?.innerWidth ?? 1024;
  return windowWidth < 768 ? '90%' : 600;
}

function openErrorModal(
  modal: NzModalService,
  document: Document,
  title: string,
  message: string,
): void {
  const now = Date.now();

  if (lastErrorMessage === message && now - lastErrorTime < ERROR_POPUP_COOLDOWN) {
    return;
  }

  if (errorModalShown) {
    return;
  }

  lastErrorMessage = message;
  lastErrorTime = now;
  errorModalShown = true;

  const modalRef = modal.create<ErrorHandleComponent, ErrorModalData>({
    nzContent: ErrorHandleComponent,

    // nzComponentParams was removed in NG-ZORRO 22.
    nzData: {
      title,
      message,
    },

    nzFooter: null,
    nzWidth: getModalWidth(document),
  });

  modalRef.afterClose.subscribe(() => {
    errorModalShown = false;
  });
}

function openConnectionErrorModal(modal: NzModalService, document: Document): void {
  if (connectionErrorShown) {
    return;
  }

  connectionErrorShown = true;

  const modalRef = modal.create<ErrorHandleComponent, ErrorModalData>({
    nzContent: ErrorHandleComponent,

    nzData: {
      title: 'Server Unavailable',
      message:
        'Unable to connect to the server. Please check your internet connection or try again later.',
    },

    nzFooter: null,
    nzWidth: getModalWidth(document),
    nzClosable: false,
    nzMaskClosable: false,
  });

  modalRef.afterClose.subscribe(() => {
    connectionErrorShown = false;
  });
}

function openSessionExpiredModal(
  modal: NzModalService,
  document: Document,
  signinService: SigninService,
  router: Router,
): void {
  if (sessionExpiredShown) {
    return;
  }

  sessionExpiredShown = true;

  const modalRef = modal.create<ErrorHandleComponent, ErrorModalData>({
    nzContent: ErrorHandleComponent,

    nzData: {
      title: 'Session Expired',
      message: 'Your session has expired. Please login again.',
    },

    nzFooter: null,
    nzWidth: getModalWidth(document),
    nzClosable: false,
    nzMaskClosable: false,
  });

  modalRef.afterClose.subscribe(() => {
    sessionExpiredShown = false;

    signinService.doLogout();
    void router.navigate(['/signin']);
  });
}

function extractErrorMessage(error: HttpErrorResponse): string {
  if (error.status === 502) {
    return 'Server is temporarily unavailable. Please try again later.';
  }

  if (error.status === 503) {
    return 'Service is currently unavailable. Please try again later.';
  }

  if (error.status === 504) {
    return 'Server response timed out. Please try again later.';
  }

  if (error.error?.message) {
    return String(error.error.message);
  }

  if (typeof error.error === 'string' && error.error.trim()) {
    try {
      const parsed: unknown = JSON.parse(error.error);

      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        'message' in parsed &&
        typeof parsed.message === 'string'
      ) {
        return parsed.message;
      }

      return error.error;
    } catch {
      return error.error;
    }
  }

  if (error.error) {
    try {
      return typeof error.error === 'object' ? JSON.stringify(error.error) : String(error.error);
    } catch (serializationError) {
      console.error('Error while extracting error message:', serializationError);
    }
  }

  return error.message || 'Something went wrong';
}

function getErrorTitle(status: number): string {
  switch (status) {
    case 400:
      return 'Validation Error';

    case 401:
      return 'Unauthorized';

    case 403:
      return 'Access Denied';

    case 404:
      return 'Record Not Found';

    case 500:
    case 502:
    case 503:
    case 504:
      return 'System Error';

    default:
      return 'Error';
  }
}

export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const modal = inject(NzModalService);
  const signinService = inject(SigninService);
  const router = inject(Router);
  const document = inject(DOCUMENT);

  return next(request).pipe(
    catchError((error: HttpErrorResponse) => {
      console.error('HTTP Status:', error.status);
      console.error('HTTP Error:', error);
      console.error('HTTP Error Body:', error.error);

      if (error.status === 0) {
        openConnectionErrorModal(modal, document);
        return throwError(() => error);
      }

      if (error.status === 401) {
        openSessionExpiredModal(modal, document, signinService, router);

        return throwError(() => error);
      }

      const message = extractErrorMessage(error).split('\n').slice(0, 3).join('\n');

      openErrorModal(modal, document, getErrorTitle(error.status), message);

      return throwError(() => error);
    }),
  );
};
